import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Severity } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { getClientConfig } from "@/services/clientConfig";
import { Divider, PrimaryButton, SecondaryButton, SectionCard } from "@/components/ds";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import IntegrationActivityLog from "../IntegrationActivityLog";

interface ContactCenterConfig {
  siteId: number;
  configured: boolean;
  accountId?: string;
  hasWebhookSecret: boolean;
}

interface Agent {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  zoomContactCenterUserId?: string;
}

const EMPTY_CONFIG: ContactCenterConfig = {
  siteId: 0,
  configured: false,
  hasWebhookSecret: false
};

const SETUP_STEPS = [
  "In the Zoom App Marketplace, create or open the General App used for this Contact Center connection.",
  "Subscribe to the voice reservation lifecycle events (created, accepted, completed, canceled, rejected, and timeout), then use the webhook URL shown below.",
  "Copy the Zoom account ID and Event Notification Secret Token into this page.",
  "Map each Contact Center agent using their Zoom Contact Center user ID, then place a real inbound queue call to verify the customer context."
];

function errorMessage(response: Response, fallback: string) {
  return response.clone().json()
    .then((body) => typeof body?.error === "string" ? body.error : `${fallback} (${response.status})`)
    .catch(() => `${fallback} (${response.status})`);
}

export default function ZoomContactCenter() {
  const api = useApi();
  const toast = useToastAlert();
  const [tab, setTab] = useState<"overview" | "setup" | "agents" | "log">("overview");
  const [config, setConfig] = useState<ContactCenterConfig>(EMPTY_CONFIG);
  const [accountId, setAccountId] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [agents, setAgents] = useState<Agent[]>([]);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [savingAgent, setSavingAgent] = useState<number | null>(null);
  const [agentsError, setAgentsError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.messaging.get("ZoomContactCenterConfig");
      if (!response.ok) throw new Error(await errorMessage(response, "Failed to load configuration"));
      const value = await response.json() as ContactCenterConfig;
      setConfig(value);
      setAccountId(value.accountId ?? "");
    } catch (error) {
      toast.displayToast(Severity.Error, error instanceof Error ? error.message : "Failed to load Contact Center configuration.");
    } finally {
      setLoading(false);
    }
  }, [api.messaging, toast]);

  const loadAgents = useCallback(async () => {
    setAgentsLoading(true);
    setAgentsError(null);
    try {
      const response = await api.messaging.get("ZoomContactCenterConfig/agents");
      if (!response.ok) throw new Error(await errorMessage(response, "Failed to load agents"));
      const value = await response.json() as Agent[];
      setAgents(value);
      setEdits(Object.fromEntries(value.map((agent) => [agent.id, agent.zoomContactCenterUserId ?? ""])));
    } catch (error) {
      setAgentsError(error instanceof Error ? error.message : "Failed to load agents.");
    } finally {
      setAgentsLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (tab === "agents") void loadAgents(); }, [tab, loadAgents]);

  async function saveConfig() {
    if (!accountId.trim()) {
      toast.displayToast(Severity.Error, "Zoom account ID is required.");
      return;
    }
    if (!config.configured && !webhookSecret.trim()) {
      toast.displayToast(Severity.Error, "Webhook Secret is required for a new connection.");
      return;
    }
    setSaving(true);
    try {
      const response = await api.messaging.put("ZoomContactCenterConfig", {
        accountId: accountId.trim(),
        webhookSecret: webhookSecret.trim() || undefined
      });
      if (!response.ok) throw new Error(await errorMessage(response, "Failed to save configuration"));
      setWebhookSecret("");
      toast.displayToast(Severity.Success, "Zoom Contact Center settings saved.");
      await load();
    } catch (error) {
      toast.displayToast(Severity.Error, error instanceof Error ? error.message : "Failed to save Contact Center settings.");
    } finally {
      setSaving(false);
    }
  }

  async function saveAgent(agentId: number) {
    setSavingAgent(agentId);
    try {
      const response = await api.messaging.put(`ZoomContactCenterConfig/agents/${agentId}`, {
        zoomContactCenterUserId: edits[agentId] ?? ""
      });
      if (!response.ok) throw new Error(await errorMessage(response, "Failed to save agent mapping"));
      toast.displayToast(Severity.Success, "Contact Center agent mapping saved.");
      await loadAgents();
    } catch (error) {
      toast.displayToast(Severity.Error, error instanceof Error ? error.message : "Failed to save agent mapping.");
    } finally {
      setSavingAgent(null);
    }
  }

  async function disconnect() {
    if (!window.confirm("Disconnect Zoom Contact Center? This revokes the stored webhook secret. Agent mappings are retained for a later reconnect.")) return;
    setSaving(true);
    try {
      const response = await api.messaging.delete("ZoomContactCenterConfig");
      if (!response.ok) throw new Error(await errorMessage(response, "Failed to disconnect Zoom Contact Center"));
      setWebhookSecret("");
      toast.displayToast(Severity.Success, "Zoom Contact Center disconnected. Agent mappings were retained.");
      await load();
    } catch (error) {
      toast.displayToast(Severity.Error, error instanceof Error ? error.message : "Failed to disconnect Zoom Contact Center.");
    } finally {
      setSaving(false);
    }
  }

  const apiBase = getClientConfig().messagingApiUrl.replace(/\/$/, "");
  const webhookUrl = config.siteId ? `${apiBase}/ZoomContactCenterWebhook/${config.siteId}` : "";
  const tabs = <>
    <IntegrationTab active={tab === "overview"} onClick={() => setTab("overview")}>Overview</IntegrationTab>
    <IntegrationTab active={tab === "setup"} onClick={() => setTab("setup")}>Setup</IntegrationTab>
    <IntegrationTab active={tab === "agents"} onClick={() => setTab("agents")}>Agent Mapping</IntegrationTab>
    <IntegrationTab active={tab === "log"} onClick={() => setTab("log")}>Activity Log</IntegrationTab>
  </>;

  return <StickyRouteHeader title="Zoom Contact Center" tabs={tabs}>
    {tab === "overview" && <>
      <SectionCard title="Queue screen pop" description="Customer context in Velaro while Zoom Contact Center owns queue routing, audio, transfer, and wrap-up." className="mb-4">
        <p className="text-xs text-zinc-500 dark:text-zinc-400">This is a separate Zoom product from Zoom Phone. Both may be configured for the same site and use independent agent identity mappings.</p>
      </SectionCard>
      <SectionCard title="Pilot readiness" description="A saved setting is not proof of delivery." className="mb-4">
        <ul className="text-xs text-zinc-500 dark:text-zinc-400 space-y-2 list-disc pl-4">
          <li>{config.configured ? "Webhook credentials are saved." : "Webhook credentials still need to be saved."}</li>
          <li>Map each queue agent, then verify a live inbound voice reservation reaches the mapped Velaro session.</li>
          <li>Verify that the caller number supplied by Zoom matches the intended NetSuite customer, including a reception-to-sales transfer.</li>
        </ul>
      </SectionCard>
      <SectionCard title="Call history" description="Inbound Contact Center screen pops appear with all other call records after the webhook is delivered.">
        <Link
          to="/reports/call-activity?provider=zoom-contact-center"
          className="inline-flex items-center rounded-lg border border-zinc-300 dark:border-zinc-600 px-3 py-2 text-xs font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
        >
          Open Contact Center call history
        </Link>
      </SectionCard>
    </>}

    {tab === "setup" && <SectionCard title="Connect Zoom Contact Center" description="Signed Contact Center reservation events, separate from Zoom Phone." className="mb-4">
      {loading ? <div className="text-sm text-zinc-400 py-4">Loading…</div> : <div className="flex flex-col gap-4">
        <div><label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">Webhook URL</label><div className="flex gap-2"><input readOnly value={webhookUrl || "Unavailable until site identity is loaded"} className="flex-1 px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-zinc-50 dark:bg-zinc-900 font-mono" />{webhookUrl && <SecondaryButton className="!text-xs !px-3 !py-2" onClick={() => { navigator.clipboard?.writeText(webhookUrl); toast.displayToast(Severity.Success, "Webhook URL copied."); }}>Copy</SecondaryButton>}</div></div>
        <div><label htmlFor="zoomCcAccountId" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">Zoom Account ID</label><input id="zoomCcAccountId" value={accountId} onChange={(event) => setAccountId(event.target.value)} className="w-full px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg" /></div>
        <div><label htmlFor="zoomCcWebhookSecret" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">Webhook Secret</label><input id="zoomCcWebhookSecret" type="password" value={webhookSecret} onChange={(event) => setWebhookSecret(event.target.value)} placeholder={config.hasWebhookSecret ? "••••••••••••• (saved — enter a new value to replace)" : "Paste the Zoom Event Notification Secret Token"} className="w-full px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg" />{config.hasWebhookSecret && <span className="text-xs text-green-600 dark:text-green-400 mt-1 inline-block">Saved</span>}</div>
        <div className="flex gap-2"><PrimaryButton onClick={() => void saveConfig()} disabled={saving}>{saving ? "Saving…" : "Save Contact Center Settings"}</PrimaryButton>{config.configured && <SecondaryButton onClick={() => void disconnect()} disabled={saving}>Disconnect</SecondaryButton>}</div>
        <Divider />
        <ol className="flex flex-col gap-2">{SETUP_STEPS.map((step, index) => <li key={step} className="flex gap-2 text-xs text-zinc-600 dark:text-zinc-300"><span className="w-5 h-5 shrink-0 rounded-full bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 text-[10px] font-bold flex items-center justify-center">{index + 1}</span><span>{step}</span></li>)}</ol>
      </div>}
    </SectionCard>}

    {tab === "agents" && <SectionCard title="Contact Center agent mapping" description="Map the Zoom Contact Center user identity; it is deliberately independent from the same person's Zoom Phone user ID.">
      {agentsLoading ? <div className="text-sm text-zinc-400 py-6 text-center">Loading agents…</div> : agentsError ? <div className="text-sm text-red-600 py-6 text-center">{agentsError}</div> : <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700"><table className="w-full text-xs"><thead><tr className="border-b border-zinc-200 dark:border-zinc-700 text-left text-zinc-500"><th className="px-3 py-2">Agent</th><th className="px-3 py-2">Zoom Contact Center User ID</th><th className="px-3 py-2" /></tr></thead><tbody>{agents.map((agent) => <tr key={agent.id} className="border-b border-zinc-100 dark:border-zinc-800"><td className="px-3 py-2"><p className="font-medium text-zinc-900 dark:text-zinc-100">{agent.firstName} {agent.lastName}</p><p className="text-zinc-400">{agent.email}</p></td><td className="px-3 py-2"><input value={edits[agent.id] ?? ""} onChange={(event) => setEdits((old) => ({ ...old, [agent.id]: event.target.value }))} placeholder="Zoom Contact Center user ID" className="w-64 text-xs border border-zinc-300 dark:border-zinc-700 rounded px-2 py-1" /></td><td className="px-3 py-2"><SecondaryButton className="!text-xs !px-3 !py-1" disabled={savingAgent === agent.id} onClick={() => void saveAgent(agent.id)}>{savingAgent === agent.id ? "Saving…" : "Save"}</SecondaryButton></td></tr>)}</tbody></table></div>}
    </SectionCard>}

    {tab === "log" && <SectionCard title="Configuration audit"><p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">Shows connection and agent-mapping changes. Use integration diagnostics after a live call to verify webhook delivery and screen-pop outcome.</p><IntegrationActivityLog integrationSlug="ZoomContactCenter" /></SectionCard>}
  </StickyRouteHeader>;
}
